/**
 * The client's domain types.
 *
 * Engine-free on purpose: nothing here imports three or IWSDK, so the rules and
 * services that use it are testable without a headset. See docs/03-DESIGN.md §3.
 */
import type { Manifest, PartRecord, SystemRecord } from "@atlas/runtime";

/** A part as the client works with it: the manifest record plus what it is indexed by. */
export interface CatalogPart {
  id: string;
  name: string;
  system: string;
  /** FMA concept id, "" when the source had none. */
  concept: string;
  side: PartRecord["side"];
  centre: readonly number[];
  bounds: readonly [readonly number[], readonly number[]];
  /** Triangles at the level the catalogue was built for, for budgeting. */
  tris: number;
  /** Lowercased name, split once, for search. */
  terms: readonly string[];
}

export interface CatalogConcept {
  id: string;
  name: string;
  parts: readonly string[];
  terms: readonly string[];
}

export interface CatalogSystem extends SystemRecord {
  parts: readonly string[];
  tris: number;
}

/** Everything the client can look something up by. */
export interface Catalog {
  id: string;
  parts: readonly CatalogPart[];
  byId: ReadonlyMap<string, CatalogPart>;
  systems: readonly CatalogSystem[];
  systemById: ReadonlyMap<string, CatalogSystem>;
  concepts: readonly CatalogConcept[];
  conceptById: ReadonlyMap<string, CatalogConcept>;
  /** Slot of each part in `parts`, which is also its index in a visibility mask. */
  slotOf: ReadonlyMap<string, number>;
  bounds: readonly [readonly number[], readonly number[]];
}

/** A named group the user can select or isolate: a system, a concept, or an ad-hoc set. */
export interface Group {
  kind: "system" | "concept" | "part" | "set";
  id: string;
  name: string;
  parts: readonly string[];
}

const WORD = /[^a-z0-9]+/;

function terms(name: string): string[] {
  return name
    .toLowerCase()
    .split(WORD)
    .filter((t) => t.length > 0);
}

/**
 * Index a manifest for the level the client is showing. Built once per bundle;
 * every rule below reads it and never mutates it.
 */
export function buildCatalog(manifest: Manifest, level: string): Catalog {
  const parts: CatalogPart[] = [];
  const slotOf = new Map<string, number>();
  const byId = new Map<string, CatalogPart>();
  const systemParts = new Map<string, string[]>();
  const systemTris = new Map<string, number>();

  for (const record of manifest.parts) {
    const range = record.levels[level];
    if (!range) continue; // a part with no geometry at this level cannot be shown
    const part: CatalogPart = {
      id: record.id,
      name: record.name,
      system: record.system,
      concept: record.concept,
      side: record.side,
      centre: record.centre,
      bounds: record.bounds as readonly [readonly number[], readonly number[]],
      tris: range.tris,
      terms: terms(record.name),
    };
    slotOf.set(part.id, parts.length);
    parts.push(part);
    byId.set(part.id, part);
    let list = systemParts.get(part.system);
    if (!list) systemParts.set(part.system, (list = []));
    list.push(part.id);
    systemTris.set(part.system, (systemTris.get(part.system) ?? 0) + part.tris);
  }

  const systems: CatalogSystem[] = manifest.systems
    .filter((s) => (systemParts.get(s.id)?.length ?? 0) > 0)
    .map((s) => ({ ...s, parts: systemParts.get(s.id) ?? [], tris: systemTris.get(s.id) ?? 0 }));

  const concepts: CatalogConcept[] = [];
  for (const c of manifest.concepts) {
    const members = c.parts.filter((id) => byId.has(id));
    if (members.length === 0) continue;
    concepts.push({ id: c.id, name: c.name, parts: members, terms: terms(c.name) });
  }

  return {
    id: manifest.id,
    parts,
    byId,
    systems,
    systemById: new Map(systems.map((s) => [s.id, s])),
    concepts,
    conceptById: new Map(concepts.map((c) => [c.id, c])),
    slotOf,
    bounds: manifest.bounds as readonly [readonly number[], readonly number[]],
  };
}

/** The group a part belongs to at each level of grouping, coarsest first. */
export function groupsFor(catalog: Catalog, partId: string): Group[] {
  const part = catalog.byId.get(partId);
  if (!part) return [];
  const out: Group[] = [];
  const system = catalog.systemById.get(part.system);
  if (system) out.push({ kind: "system", id: system.id, name: system.name, parts: system.parts });
  const concept = catalog.conceptById.get(part.concept);
  if (concept && concept.parts.length > 1) out.push({ kind: "concept", id: concept.id, name: concept.name, parts: concept.parts });
  out.push({ kind: "part", id: part.id, name: part.name, parts: [part.id] });
  return out;
}
